import { Stack, Typography, Link, Skeleton, useTheme, LinkProps } from "@mui/material";
import { ComponentType, FC } from "react";
import { SenecFPPTheme } from "src/styles/fpp-theme/theme.types";
import SvgIcon, { SvgIconProps } from "@mui/material/SvgIcon";

export type SenecLinkTileProps = {
    /** The title displayed on the tile. */
    title: string;
    /** The URL the tile links to. */
    linkAddress: string;
    /** Whether the tile is in a loading state. */
    isLoading?: boolean;
    /** If true, opens the link in a new tab. */
    openLinkInNewTab?: boolean;
    /** The pictogram component to display. */
    PictogramComponent: ComponentType;
    /** @deprecated Please use `PictogramComponent` instead. */
    icon?: FC<SvgIconProps>;
    /** The system prop for styling. */
} & Pick<LinkProps, "sx">;

/**
 * The `SenecLinkTile` component is used to display a tile with a link, a pictogram, and a title
 */
export const SenecLinkTile = ({
    title,
    linkAddress,
    icon,
    PictogramComponent,
    isLoading = false,
    openLinkInNewTab = false,
    sx,
}: SenecLinkTileProps) => {
    const theme: SenecFPPTheme = useTheme();

    if (isLoading) {
        return <Skeleton data-testid="skeleton" variant="rounded" sx={{ borderRadius: "0.75rem" }} width="100%" height="9rem" />;
    }

    return (
        <Link
            width={"100%"}
            href={linkAddress}
            underline="none"
            target={openLinkInNewTab ? "_blank" : "_self"}
            sx={{
                display: "flex",
                padding: 3,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: theme.palette.neutralGray20.main,
                backgroundColor: theme.palette.white.main,
                "&:hover": {
                    backgroundColor: "neutralGray000.main",
                },
                "&:active": {
                    backgroundColor: "neutralGray05.main",
                },
                ...(sx ?? {}),
            }}
        >
            <Stack color={theme.palette.deepBlue.main}>
                <Typography mb={2} variant="h3">
                    {title}
                </Typography>

                {PictogramComponent && <PictogramComponent />}

                {icon && <SvgIcon component={icon} data-testid="link-tile-icon" sx={{ fontSize: 36 }} />}
            </Stack>
        </Link>
    );
};
